import React, { useState, useRef, useCallback, useEffect } from 'react';
import { X, Camera, ZoomIn, ZoomOut } from 'lucide-react';
import { motion } from 'framer-motion';

interface ImageCropperProps {
  imageFile: File;
  onCancel: () => void;
  onCropComplete: (croppedImageBlob: Blob, croppedImageUrl: string) => void;
}

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const OUTPUT_SIZE = 800; // Output resolution for avatar
const MIN_DIMENSION = 200; // Minimum image dimension

const ImageCropper: React.FC<ImageCropperProps> = ({ imageFile, onCancel, onCropComplete }) => {
  const [imageSrc, setImageSrc] = useState<string>('');
  const [imageLoaded, setImageLoaded] = useState(false);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [error, setError] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState(false);

  // Pinch zoom state
  const [initialPinchDistance, setInitialPinchDistance] = useState<number | null>(null);
  const [initialScale, setInitialScale] = useState(1);

  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Validate and load image
  useEffect(() => {
    // Validate file type
    if (!ALLOWED_TYPES.includes(imageFile.type)) {
      setError('Please select a valid image file (JPG, PNG, or WebP)');
      return;
    }

    // Validate file size
    if (imageFile.size > MAX_FILE_SIZE) {
      setError('Image is too large. Please choose an image under 10MB.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target?.result) {
        const imgSrc = e.target.result as string;

        // Validate image dimensions
        const img = new Image();
        img.onload = () => {
          if (img.naturalWidth < MIN_DIMENSION || img.naturalHeight < MIN_DIMENSION) {
            setError(`Image is too small. Minimum size is ${MIN_DIMENSION}x${MIN_DIMENSION} pixels.`);
            return;
          }
          setImageSrc(imgSrc);
        };
        img.onerror = () => {
          setError('Failed to load image. Please try another file.');
        };
        img.src = imgSrc;
      }
    };
    reader.onerror = () => {
      setError('Failed to read file. Please try again.');
    };
    reader.readAsDataURL(imageFile);
  }, [imageFile]);

  // Auto-fit image when loaded - FIT TO FRAME, NO AGGRESSIVE ZOOM
  useEffect(() => {
    if (!imageSrc || !imageRef.current || !containerRef.current) return;

    const img = imageRef.current;
    const handleImageLoad = () => {
      const container = containerRef.current;
      if (!container) return;

      const containerWidth = container.clientWidth;
      const containerHeight = container.clientHeight;
      const imgWidth = img.naturalWidth;
      const imgHeight = img.naturalHeight;

      // Calculate scale to fit the ENTIRE image within the square frame
      // This ensures nothing is cropped by default
      const scaleX = containerWidth / imgWidth;
      const scaleY = containerHeight / imgHeight;

      // Use the SMALLER scale to ensure entire image fits
      const initialScale = Math.min(scaleX, scaleY);

      setScale(initialScale);
      setPosition({ x: 0, y: 0 });
      setImageLoaded(true);
    };

    if (img.complete && img.naturalWidth > 0) {
      handleImageLoad();
    } else {
      img.addEventListener('load', handleImageLoad);
      return () => img.removeEventListener('load', handleImageLoad);
    }
  }, [imageSrc]);

  // Mouse drag handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
  };

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging) return;
    e.preventDefault();
    setPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y,
    });
  }, [isDragging, dragStart]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // Touch handlers for mobile
  const getTouchDistance = (touches: TouchList) => {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      // Pinch to zoom
      e.preventDefault();
      const distance = getTouchDistance(e.touches);
      setInitialPinchDistance(distance);
      setInitialScale(scale);
    } else if (e.touches.length === 1) {
      // Single touch to drag
      const touch = e.touches[0];
      setIsDragging(true);
      setDragStart({ x: touch.clientX - position.x, y: touch.clientY - position.y });
    }
  };

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (e.touches.length === 2 && initialPinchDistance !== null) {
      // Pinch zoom
      e.preventDefault();
      const distance = getTouchDistance(e.touches);
      const newScale = initialScale * (distance / initialPinchDistance);
      setScale(Math.max(0.5, Math.min(5, newScale)));
    } else if (e.touches.length === 1 && isDragging) {
      // Drag
      e.preventDefault();
      const touch = e.touches[0];
      setPosition({
        x: touch.clientX - dragStart.x,
        y: touch.clientY - dragStart.y,
      });
    }
  }, [isDragging, dragStart, initialPinchDistance, initialScale]);

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
    setInitialPinchDistance(null);
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener('touchmove', handleTouchMove, { passive: false });
      container.addEventListener('touchend', handleTouchEnd);
      return () => {
        container.removeEventListener('touchmove', handleTouchMove);
        container.removeEventListener('touchend', handleTouchEnd);
      };
    }
  }, [handleTouchMove, handleTouchEnd]);

  // Scroll wheel zoom
  const handleWheel = useCallback((e: WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY * -0.01;
    const newScale = Math.max(0.5, Math.min(5, scale + delta));
    setScale(newScale);
  }, [scale]);

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener('wheel', handleWheel, { passive: false });
      return () => container.removeEventListener('wheel', handleWheel);
    }
  }, [handleWheel]);

  // Crop and compress image
  const getCroppedImage = async () => {
    if (!imageRef.current || !canvasRef.current || !containerRef.current) return;

    setIsProcessing(true);

    try {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Could not get canvas context');

      const img = imageRef.current;
      const container = containerRef.current;

      // Set output size
      canvas.width = OUTPUT_SIZE;
      canvas.height = OUTPUT_SIZE;

      // Calculate the visible area of the image within the square crop frame
      const containerRect = container.getBoundingClientRect();
      const imgRect = img.getBoundingClientRect();

      // Calculate the source coordinates on the original image
      const scaleRatio = img.naturalWidth / imgRect.width;

      const sourceX = (containerRect.left - imgRect.left) * scaleRatio;
      const sourceY = (containerRect.top - imgRect.top) * scaleRatio;
      const sourceWidth = containerRect.width * scaleRatio;
      const sourceHeight = containerRect.height * scaleRatio;

      // Draw the cropped portion
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, OUTPUT_SIZE, OUTPUT_SIZE);

      ctx.drawImage(
        img,
        sourceX,
        sourceY,
        sourceWidth,
        sourceHeight,
        0,
        0,
        OUTPUT_SIZE,
        OUTPUT_SIZE
      );

      // Convert to blob with compression
      canvas.toBlob(
        (blob) => {
          if (blob) {
            const croppedUrl = URL.createObjectURL(blob);
            onCropComplete(blob, croppedUrl);
          } else {
            setError('Failed to process image. Please try again.');
            setIsProcessing(false);
          }
        },
        'image/jpeg',
        0.85 // Good quality with reasonable file size
      );
    } catch (err) {
      console.error('Error cropping image:', err);
      setError('Failed to process image. Please try again.');
      setIsProcessing(false);
    }
  };

  const handleZoomIn = () => {
    setScale(prev => Math.min(5, prev + 0.2));
  };

  const handleZoomOut = () => {
    setScale(prev => Math.max(0.5, prev - 0.2));
  };

  if (error) {
    return (
      <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-gray-900 rounded-2xl p-6 max-w-md w-full border border-white/10"
        >
          <h3 className="text-white font-bold text-lg mb-2">Image Error</h3>
          <p className="text-white/70 mb-6">{error}</p>
          <button
            onClick={onCancel}
            className="w-full py-3 bg-accent2 hover:bg-accent2/90 text-white font-semibold rounded-xl transition-colors"
          >
            Choose Another Photo
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/95 z-50 flex flex-col">
      <canvas ref={canvasRef} className="hidden" />

      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-white/10">
        <button
          onClick={onCancel}
          disabled={isProcessing}
          className="text-white/70 hover:text-white transition-colors disabled:opacity-50"
          aria-label="Cancel"
        >
          <X size={24} />
        </button>
        <h2 className="text-white font-bold text-lg">Edit Photo</h2>
        <button
          onClick={getCroppedImage}
          disabled={!imageLoaded || isProcessing}
          className="text-accent2 hover:text-accent2/80 font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isProcessing ? 'Saving...' : 'Done'}
        </button>
      </div>

      {/* Main crop area */}
      <div className="flex-1 flex items-center justify-center p-4 overflow-hidden">
        <div className="flex flex-col lg:flex-row items-center gap-6 w-full max-w-6xl">
          {/* Square crop frame */}
          <div className="relative">
            <div
              ref={containerRef}
              className="relative bg-black overflow-hidden touch-none"
              style={{
                width: 'min(90vw, 500px)',
                height: 'min(90vw, 500px)',
                maxWidth: '500px',
                maxHeight: '500px',
              }}
            >
              {/* Crop overlay - square frame stays fixed */}
              <div className="absolute inset-0 pointer-events-none z-10">
                <div className="absolute inset-0 border-2 border-white/50 rounded-lg" />
                <div className="absolute top-1/3 left-0 right-0 h-px bg-white/20" />
                <div className="absolute top-2/3 left-0 right-0 h-px bg-white/20" />
                <div className="absolute left-1/3 top-0 bottom-0 w-px bg-white/20" />
                <div className="absolute left-2/3 top-0 bottom-0 w-px bg-white/20" />
              </div>

              {/* Image that moves inside the frame */}
              <div className="absolute inset-0 flex items-center justify-center">
                {imageSrc && (
                  <img
                    ref={imageRef}
                    src={imageSrc}
                    alt="Crop preview"
                    className="max-w-none cursor-move select-none"
                    style={{
                      transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
                      transformOrigin: 'center',
                    }}
                    onMouseDown={handleMouseDown}
                    onTouchStart={handleTouchStart}
                    draggable={false}
                  />
                )}
              </div>

              {!imageLoaded && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white"></div>
                </div>
              )}
            </div>
          </div>

          {/* Live circular preview */}
          <div className="flex flex-col items-center gap-4">
            <div>
              <p className="text-white/60 text-sm mb-2 text-center">Preview</p>
              <div
                className="relative overflow-hidden rounded-full bg-gray-800 border-2 border-white/20"
                style={{ width: '120px', height: '120px' }}
              >
                {imageSrc && imageLoaded && (
                  <img
                    src={imageSrc}
                    alt="Avatar preview"
                    className="absolute max-w-none select-none pointer-events-none"
                    style={{
                      transform: `translate(${position.x * 0.24}px, ${position.y * 0.24}px) scale(${scale * 0.24})`,
                      transformOrigin: 'center',
                      left: '50%',
                      top: '50%',
                      marginLeft: '-50%',
                      marginTop: '-50%',
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="p-6 space-y-4 border-t border-white/10 bg-gray-900/50">
        {/* Zoom slider */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <label className="text-white text-sm font-medium flex items-center gap-2">
              <ZoomOut size={16} className="text-white/60" />
              Zoom
            </label>
            <div className="flex items-center gap-2">
              <span className="text-white/60 text-sm font-mono">{Math.round(scale * 100)}%</span>
              <ZoomIn size={16} className="text-white/60" />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleZoomOut}
              className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
              aria-label="Zoom out"
            >
              <ZoomOut size={20} className="text-white" />
            </button>
            <input
              type="range"
              min="0.5"
              max="5"
              step="0.1"
              value={scale}
              onChange={(e) => setScale(parseFloat(e.target.value))}
              className="flex-1 h-2 bg-white/10 rounded-lg appearance-none cursor-pointer slider"
            />
            <button
              onClick={handleZoomIn}
              className="p-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
              aria-label="Zoom in"
            >
              <ZoomIn size={20} className="text-white" />
            </button>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-3">
          <button
            onClick={onCancel}
            disabled={isProcessing}
            className="flex-1 py-3 bg-white/10 hover:bg-white/20 text-white font-semibold rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Change Photo
          </button>
          <button
            onClick={getCroppedImage}
            disabled={!imageLoaded || isProcessing}
            className="flex-1 py-3 bg-accent2 hover:bg-accent2/90 text-white font-semibold rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            <Camera size={20} />
            {isProcessing ? 'Processing...' : 'Use This Photo'}
          </button>
        </div>

        {/* Instructions */}
        <p className="text-white/50 text-center text-xs leading-relaxed">
          Drag to reposition • Use slider or pinch to zoom • Scroll wheel to zoom on desktop
        </p>
      </div>

      <style>{`
        .slider::-webkit-slider-thumb {
          appearance: none;
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: white;
          cursor: pointer;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        }
        .slider::-moz-range-thumb {
          width: 24px;
          height: 24px;
          border-radius: 50%;
          background: white;
          cursor: pointer;
          border: none;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        }
        .slider::-webkit-slider-track {
          background: rgba(255,255,255,0.1);
          border-radius: 4px;
          height: 8px;
        }
        .slider::-moz-range-track {
          background: rgba(255,255,255,0.1);
          border-radius: 4px;
          height: 8px;
        }
      `}</style>
    </div>
  );
};

export default ImageCropper;
